import React, { useState } from 'react';
import { api, useAuth } from '../../context/AuthContext';
import { Brain, Loader2, X, Check } from 'lucide-react';

// Tombol "Save to memory" di bawah setiap jawaban AI (semua chat).
// Admin  → langsung aktif (bisa pilih scope & pin).
// Non-admin → usulan pending, hanya untuk dirinya; admin me-review di AI Configuration → Memory.
const errMsg = (e) => (e && e.response && e.response.data && (e.response.data.error || e.response.data.message)) || (e && e.message) || String(e);

function firstLine(text) {
  const l = String(text || '').split('\n').map(s => s.replace(/^[#>*\-\s]+/, '').trim()).find(Boolean) || '';
  return l.length > 90 ? l.slice(0, 87) + '…' : l;
}

export default function SaveToMemoryButton({ content }) {
  let auth = {};
  try { auth = useAuth() || {}; } catch (_) { auth = {}; }
  const isAdmin = !!(auth.user && auth.user.role === 'admin');

  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');

  const start = () => {
    setErr(''); setDone('');
    setF({ title: firstLine(content), content: String(content || '').slice(0, 4000), kind: 'fact', scope: 'global', scope_key: '', tags: '', pinned: false });
    setOpen(true);
  };
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await api.post('/api/ai/memory', f);
      if (data && data.ok === false) { setErr(data.message || 'Not saved.'); setBusy(false); return; }
      setDone(data && data.status === 'pending' ? 'Sent for admin review' : 'Saved to memory');
      setOpen(false);
      setTimeout(() => setDone(''), 4000);
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  return (
    <>
      <button onClick={start} title="Save this answer as a long-term memory"
        className="flex items-center gap-1 text-xs text-slate-400 hover:text-brand-700">
        {done ? <Check style={{ width: 11, height: 11 }} /> : <Brain style={{ width: 11, height: 11 }} />}
        {done || 'Save to memory'}
      </button>

      {open && f && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => !busy && setOpen(false)}>
          <div className="w-full max-w-lg bg-white rounded-xl shadow-xl p-4 space-y-3 text-left" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Brain size={15} className="text-brand-600" /> Save to memory</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
            </div>
            <p className="text-[11px] text-slate-500">
              {isAdmin
                ? 'Edit it down to the durable part (root cause, rule, procedure). It becomes active immediately.'
                : 'Edit it down to the durable part. It is saved for you only and an admin reviews it before the AI uses it.'}
            </p>
            <input value={f.title} onChange={e => set('title', e.target.value)} placeholder="Title (one line)"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" />
            <textarea value={f.content} onChange={e => set('content', e.target.value)} rows={8}
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg font-mono" />
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-slate-500">Kind
                <select value={f.kind} onChange={e => set('kind', e.target.value)} className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg">
                  <option value="fact">Fact</option>
                  <option value="skill">Skill (procedure)</option>
                </select>
              </label>
              <label className="text-xs text-slate-500">Tags
                <input value={f.tags} onChange={e => set('tags', e.target.value)} placeholder="p01,performance"
                  className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg" />
              </label>
              {isAdmin && (<>
                <label className="text-xs text-slate-500">Scope
                  <select value={f.scope} onChange={e => set('scope', e.target.value)} className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg">
                    <option value="global">Global (all chats)</option>
                    <option value="source">Dashboard / datasource</option>
                    <option value="user">User</option>
                  </select>
                </label>
                <label className="text-xs text-slate-500">{f.scope === 'user' ? 'Username' : 'Datasource key'}
                  <input value={f.scope === 'global' ? '' : f.scope_key} disabled={f.scope === 'global'} onChange={e => set('scope_key', e.target.value)}
                    className="mt-1 w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg disabled:bg-slate-50" />
                </label>
              </>)}
            </div>
            {isAdmin && (
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={!!f.pinned} onChange={e => set('pinned', e.target.checked)} /> Pinned (always injected)
              </label>
            )}
            {err && <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{err}</div>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={submit} disabled={busy || !f.title.trim() || !f.content.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg disabled:opacity-50">
                {busy && <Loader2 size={14} className="animate-spin" />} {isAdmin ? 'Save' : 'Submit for review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
